/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';

import {
  blueGrey,
  grey,
} from '@mui/material/colors';
import {
  DatePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  Add,
  CloudDownload,
  Edit,
  InfoOutlined,
  Visibility,
} from '@mui/icons-material';
import {
  groupBy,
  uniqBy,
} from 'lodash';
import TenantAvatar from '../../../../../../components/Tenant/TenantAvatar';
import { primary } from '../../../../../../utils/brandColors';
import InvoiceStatusSelectComponent from '../../../../../../components/EnumSelects/InvoiceStatusSelect';
import BarnUsersAutocomplete from '../../../../../../components/Autocompletes/BarnUsers';
import UserProfile from '../../../../../settings/UserManagementPage/components/UserProfile';
import Horse from '../../../../../../components/Horse';
import HorseBillableSelectPopover from '../../../HorseBillableSelectPopover';
import {
  addInvoice,
  deleteInvoice,
  getInvoicePdf,
  updateInvoice,
} from '../../../../../../redux/invoice/actions';
import PdfView from '../../../Invoice/components/PdfView';
import AddEditBillableModalComponent from '../../../AddEditBillableModal';

function sumArray(arr) {
  let total = 0;
  for (let i = 0; i < arr.length; i += 1) {
    total += arr[i];
  }
  return total;
}
const AddEditInvoiceModalComponent = ({
  open,
  onClose,
  invoice,
}) => {
  const dispatch = useDispatch();
  // Redux
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const isUpdatingInvoice = useSelector((state) => state.invoice.isUpdatingInvoice);
  const isAddingInvoice = useSelector((state) => state.invoice.isAddingInvoice);
  const isDownloadingInvoicePdf = useSelector((state) => state.invoice.isDownloadingInvoicePdf);
  const invoiceHeaders = useSelector((state) => state.invoiceHeader.invoiceHeaders);
  const isLoading = isUpdatingInvoice || isAddingInvoice;

  // form fields
  const [preview, setPreview] = React.useState(false);
  const [title, setTitle] = React.useState(selectedBarn.name);
  const [status, setStatus] = React.useState('Open');
  const [barnUsers, setBarnUsers] = React.useState([]);
  const [horseBillables, setHorseBillables] = React.useState([]);
  const [addDiscount, setAddDiscount] = React.useState(false);
  const [discountAmount, setDiscountAmount] = React.useState(0);
  const [discountPercent, setDiscountPercent] = React.useState(0);
  const [discountDescription, setDiscountDescription] = React.useState('');
  const [invoiceDate, setInvoiceDate] = React.useState(moment().startOf('day').toDate());
  const [groupHorseBillablesByHorse, setGroupHorseBillablesByHorse] = React.useState(false);
  const [groupHorseBillablesByTitle, setGroupHorseBillablesByTitle] = React.useState(false);
  const [dueDate, setDueDate] = React.useState(moment().startOf('day').add(1, 'months').toDate());
  const [notes, setNotes] = React.useState('');
  const [addingInvoiceError, setAddingInvoiceError] = React.useState(null);
  const [validation, setValidation] = React.useState({});
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [openUserModal, setOpenUserModal] = React.useState(false);
  const [selectedBarnUser, setSelectedBarnUser] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openBillablesPopover, setOpenBillablePopover] = React.useState(false);
  const [sendEmail, setSendEmail] = React.useState(true);
  const [snackbar, setSnackbar] = React.useState(null);
  const [openAddEditBillableModal, setOpenAddEditBillableModal] = React.useState(false);
  const [selectedBillable, setSelectedBillable] = React.useState(null);
  const [selectedInvoiceHeader, setSelectedInvoiceHeader] = React.useState(null);

  React.useEffect(() => {
    setSendEmail(true);
    setPreview(false)
    setTitle((invoice && invoice.title) || selectedBarn.name)
    setStatus((invoice && invoice.status) || 'Draft');
    setBarnUsers((invoice && invoice.barnUsers) || [])
    setHorseBillables((invoice && invoice.horseBillablesSorted) || [])
    setAddDiscount((invoice && (invoice.discountAmount || invoice.discountPercent)) || false);
    setDiscountAmount((invoice && invoice.discountAmount) || 0);
    setDiscountPercent((invoice && invoice.discountPercent) || 0);
    setDiscountDescription((invoice && invoice.discountDescription) || '');
    setInvoiceDate((invoice && invoice.invoiceDate) || moment().startOf('day').toDate());
    setDueDate((invoice && invoice.dueDate) || moment().startOf('day').add(1, 'months').toDate());
    setNotes((invoice && invoice.notes) || '');
    setSelectedInvoiceHeader((invoice && invoice.invoiceHeader) || null)
    setGroupHorseBillablesByHorse((invoice && invoice.groupHorseBillablesByHorse) || false);
    setGroupHorseBillablesByTitle((invoice && invoice.groupHorseBillablesByTitle) || false);
    setSelectedBillable(null);
    setOpenAddEditBillableModal(false);

    setAddingInvoiceError(null);
    setValidation({});
  }, [invoice]);

  React.useEffect(() => {
    setTitle(selectedInvoiceHeader
      ? selectedInvoiceHeader.name
      : selectedBarn.name)
  }, [selectedInvoiceHeader])

  const validateFields = () => {
    const updatedValidation = { horse: '' };
    let isValid = true;
    if (horseBillables.length < 1) {
      updatedValidation.horseBillables = 'Select at least one transaction for this invoice';
      isValid = false;
    }
    if (barnUsers.length < 1) {
      updatedValidation.barnUsers = 'Select at least one user for this invoice';
      isValid = false;
    } else if (barnUsers.find((bu) => !bu.email) && status === 'Open') {
      updatedValidation.barnUsers = 'One of the selected customers does not have an email. Customers must have an email to receive invoices.';
      isValid = false;
    }
    if (!title || title.length === 0) {
      updatedValidation.title = 'Provide an invoice title';
      isValid = false;
    }
    setValidation(updatedValidation);
    return isValid;
  };

  const onSave = () => {
    if (!validateFields()) { return; }
    if (!invoice) {
      dispatch(addInvoice({
        barnUsers: barnUsers.map((bu) => bu.id),
        horseBillables: horseBillables.map((hb) => hb.id),
        status,
        discountAmount: discountAmount || null,
        discountDescription: discountDescription || null,
        discountPercent: discountPercent || null,
        invoiceDate,
        dueDate,
        notes,
        title,
        sendEmail,
        groupHorseBillablesByHorse,
        groupHorseBillablesByTitle,
        invoiceHeaderId: selectedInvoiceHeader && selectedInvoiceHeader.id,
      })).then((newInvoice) => {
        setAddingInvoiceError(false);
        onClose(newInvoice);
      }).catch((error) => {
        setAddingInvoiceError(error.message || 'There was an error attempting to add the invoice. Please contact Questri support.');
        console.error(error);
      })
    } else {
      dispatch(updateInvoice(invoice.id, {
        barnUsers: barnUsers.map((bu) => bu.id),
        horseBillables: horseBillables.map((hb) => hb.id),
        status,
        discountAmount: discountAmount || null,
        discountDescription: discountDescription || null,
        discountPercent: discountPercent || null,
        invoiceDate,
        dueDate,
        notes,
        title,
        sendEmail,
        groupHorseBillablesByHorse,
        groupHorseBillablesByTitle,
        invoiceHeaderId: selectedInvoiceHeader && selectedInvoiceHeader.id,
      })).then((updatedBillable) => {
        setAddingInvoiceError(false);
        onClose(updatedBillable);
      }).catch((error) => {
        setAddingInvoiceError(error.message || 'There was an error attempting to update the invoice. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  const handleDelete = () => {
    dispatch(deleteInvoice(invoice.id)).then(() => {
      setConfirmDelete(false);
      setAddingInvoiceError(false);
      onClose();
    }).catch(() => {
      // TODO throw error  messsage
      setAddingInvoiceError('There was an erroring deleting this invoice. Please contact support.');
      setConfirmDelete(false)
    })
  }

  const getSubtotal = () => {
    let total = 0;
    for (let i = 0; i < horseBillables.length; i += 1) {
      total += parseFloat(horseBillables[i].price);
    }
    return parseFloat(total).toFixed(2);
  }

  const getAmountDue = () => {
    let total = 0;
    for (let i = 0; i < horseBillables.length; i += 1) {
      total += parseFloat(horseBillables[i].price);
    }
    if (discountAmount) {
      total -= discountAmount;
    } else if (discountPercent) {
      total -= (total * (discountPercent / 100))
    }
    return parseFloat(total).toFixed(2);
  }

  const isEditable = !invoice || invoice.status === 'Draft'

  const groupedByType = groupBy(horseBillables, 'note');
  const groupedByHorse = groupBy(horseBillables, 'horse.id')

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      aria-labelledby="modal-add-invoice-title"
      aria-describedby="modal-add-invoice-description"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>{
            invoice
              ? 'Edit '
              : 'Add '
          }
            Invoice
          </Box>
          <Box>
            <FormControl
              size="small"
              sx={{ mr: 2 }}
            >
              <InputLabel id="demo-select-small-label">Invoice Header</InputLabel>
              <Select
                labelId="demo-select-small-label"
                id="demo-select-small"
                disabled={isLoading || !isEditable}
                value={(selectedInvoiceHeader && selectedInvoiceHeader.id) || -1}
                label="Invoice Header"
                onChange={(e) => {
                  setSelectedInvoiceHeader(invoiceHeaders.find((ih) => ih.id === e.target.value) || null)
                }}
              >
                <MenuItem value={-1}>
                  {selectedBarn.name}{' '}<em>(Default)</em>
                </MenuItem>
                {
                  invoiceHeaders.map((ih) => (
                    <MenuItem value={ih.id} key={`invoice-header-option-${ih.id}`}>{ih.name}</MenuItem>
                  ))
                }
                <Divider />
                <MenuItem value={-2} disabled>
                  <em>You can add new invoice headers from the</em>
                  <em><b>Invocie Headers & Billing Information</b> section below invoices table screen</em>
                </MenuItem>
              </Select>
            </FormControl>
            <LoadingButton
              onClick={() => {
                dispatch(getInvoicePdf(invoice.id))
              }}
              sx={{ mr: 2 }}
              disabled={isDownloadingInvoicePdf || !invoice || !invoice.id}
              variant="outlined"
              color="primary"
              loading={isDownloadingInvoicePdf}
              loadingPosition="start"
              startIcon={<CloudDownload />}
            >
              {(invoice && invoice.id)
                ? 'Download'
                : 'Download Available After Saving'}
            </LoadingButton>
            <Button
              startIcon={preview
                ? <Edit />
                : <Visibility />}
              variant="outlined"
              onClick={() => {
                if (!validateFields()) { return; } setPreview(!preview);
              }}
            >{preview
              ? 'Edit'
              : 'Preview'}
            </Button>
          </Box>

          {!!snackbar && (
            <Snackbar
              open
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              onClose={() => { setSnackbar(null); }}
              autoHideDuration={10000}
            >
              <Alert {...snackbar} onClose={() => { setSnackbar(null); }} />
            </Snackbar>
          )}
        </Box>

      </DialogTitle>
      {
        preview
          ? (
            <DialogContent
              sx={{
                p: 4,
                pt: '16px !important',
                backgroundColor: blueGrey[50],
              }}
            >
              <PdfView
                invoice={{
                  barn: selectedBarn,
                  barnUsers,
                  horseBillables,
                  horseBillablesSorted: (invoice && invoice.horseBillablesSorted) || horseBillables,
                  status,
                  discountAmount: discountAmount || null,
                  discountDescription: discountDescription || null,
                  discountPercent: discountPercent || null,
                  invoiceDate,
                  dueDate,
                  notes,
                  title,
                  id: invoice && invoice.id,
                  finalAmount: getAmountDue(),
                  invoiceHeader: selectedInvoiceHeader,
                  groupHorseBillablesByHorse,
                  groupHorseBillablesByTitle,
                }}
              />
            </DialogContent>
          )
          : (
            <DialogContent
              sx={{
                p: 4,
                pt: '16px !important',
                backgroundColor: blueGrey[50],
              }}
            >
              {
                !isEditable && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This invoice is <b>finalized</b> and cannot be modified. Only the status can be changed.
                  </Alert>
                )
              }
              <Paper
                sx={{
                  width: 840,
                  p: 2,
                  m: 'auto',
                }}
              >
                <Grid container spacing={6} justifyContent="space-between">
                  <Grid item xs={7}>
                    <Box
                      sx={{
                        display: 'flex',
                        mb: 2,
                      }}
                    >
                      <Box sx={{ mt: '8px' }}>
                        <TenantAvatar
                          tenant={{
                            displayName: selectedBarn.name,
                            iconColor: primary.main,
                            avatar: (selectedInvoiceHeader && selectedInvoiceHeader.logo && `https://${selectedInvoiceHeader.logo}`) || `https://d8adkpqcrx2wo.cloudfront.net/${selectedBarn.tenant}.png`,
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          ml: 2,
                          flex: 1,
                        }}
                      >
                        <TextField
                          sx={{ mb: 2 }}
                          fullWidth
                          disabled={isLoading || !isEditable}
                          id="invoice-title"
                          label="Invoice Title"
                          error={validation.title}
                          helperText={(validation && validation.title)}
                          placeholder="Invoice Title..."
                          value={title}
                          onChange={(e) => { setTitle(e.target.value); }}
                        />

                        <Box sx={{ mb: 2 }}>
                          <InvoiceStatusSelectComponent
                            value={status || ''}
                            disabled={isLoading}
                            includeDraftOption={(!invoice || !invoice.id) || invoice.status === 'Draft'}
                            onChange={(e) => {
                              setStatus(e.target.value)
                            }}
                          />
                        </Box>
                        <BarnUsersAutocomplete
                          multiple
                          value={barnUsers}
                          size="large"
                          disabled={isLoading || !isEditable}
                          onSelect={(e, newBarnUsers) => {
                            setBarnUsers(newBarnUsers.filter((bu) => bu.id))
                          }}
                          limitTags={2}
                          label="Customer(s) (Bill To)"
                          showAdornment
                          validation={validation.barnUsers}
                        />
                        {
                          isEditable && barnUsers.map((bu) => (
                            <Button
                              sx={{
                                mt: 0.5,
                                mr: 1,
                              }}
                              key={`barn-user-invoice-${bu.id}`}
                              endIcon={<Edit />}
                              size="small"
                              onClick={(updatedUser) => {
                                setSelectedBarnUser(bu);
                                setOpenUserModal(true);
                                if (updatedUser && updatedUser.id && updatedUser.userFullName) {
                                  setBarnUsers(barnUsers.map((b) => (b.id === updatedUser.id
                                    ? updatedUser
                                    : b)))
                                }
                              }}
                            >Edit {bu.userFullName}
                            </Button>
                          ))
                        }
                        <UserProfile
                          tenant={selectedBarn.tenant}
                          tenantUser={selectedBarnUser}
                          open={openUserModal}
                          onClose={() => { setOpenUserModal(false); setSelectedBarnUser(null) }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={5}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DatePicker
                            label="Invoice Date"
                            value={invoiceDate}
                            disabled={isLoading || !isEditable}
                            onChange={(newValue) => {
                              if (newValue && newValue.toDate) {
                                setInvoiceDate(moment(newValue.toDate()).startOf('day').toDate());
                              }
                            }}
                            onClick={(e) => { e.stopPropagation(); }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                onClick={(e) => { e.stopPropagation(); }}
                                disabled={isLoading}
                                fullWidth
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DatePicker
                            label="Due Date"
                            disabled={isLoading || !isEditable}
                            value={dueDate}
                            onChange={(newValue) => {
                              if (newValue && newValue.toDate) {
                                setDueDate(moment(newValue.toDate()).startOf('day').toDate());
                              }
                            }}
                            onClick={(e) => { e.stopPropagation(); }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                onClick={(e) => { e.stopPropagation(); }}
                                disabled={isLoading}
                                fullWidth
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>
                  </Grid>

                </Grid>
                <Divider
                  sx={{
                    mt: 4,
                    mb: 4,
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <FormControlLabel
                    control={(
                      <Radio
                        checked={!groupHorseBillablesByTitle && !groupHorseBillablesByHorse}
                        onChange={() => {
                          setGroupHorseBillablesByTitle(false);
                          setGroupHorseBillablesByHorse(false);
                        }}
                        disabled={isLoading || !isEditable}
                      />
                    )}
                    label="No Transaction Grouping"
                  />
                  <FormControlLabel
                    control={(
                      <Radio
                        checked={groupHorseBillablesByTitle}
                        onChange={() => {
                          setGroupHorseBillablesByTitle(!groupHorseBillablesByTitle);
                          if (!groupHorseBillablesByTitle) {
                            setGroupHorseBillablesByHorse(false)
                          }
                        }}
                        disabled={isLoading || !isEditable}
                      />
                    )}
                    label="Group By Transaction Description"
                  />
                  <FormControlLabel
                    control={(
                      <Radio
                        checked={groupHorseBillablesByHorse}
                        onChange={() => {
                          setGroupHorseBillablesByHorse(!groupHorseBillablesByHorse);
                          if (!groupHorseBillablesByHorse) {
                            setGroupHorseBillablesByTitle(false)
                          }
                        }}
                        disabled={isLoading || !isEditable}
                      />
                    )}
                    label="Group By Horse"
                  />
                </Box>
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 700 }} aria-label="customized table">
                    <TableHead
                      sx={{ backgroundColor: primary[500] }}
                    >
                      <TableRow>
                        <TableCell
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                          }}
                        >Horse
                        </TableCell>
                        <TableCell
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                          }}
                        >Date
                        </TableCell>
                        <TableCell
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                          }}
                        >Transaction (Description)
                        </TableCell>
                        <TableCell
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                          }}
                          align="right"
                        >Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {
                        !groupHorseBillablesByHorse && !groupHorseBillablesByTitle && horseBillables.map((hb) => (
                          <TableRow
                            key={`invoice-billable-${hb.id}`}
                            onClick={(e) => { if (isEditable) { setAnchorEl(e.currentTarget); setOpenBillablePopover(true); } }}
                            sx={{
                              '&:hover': {
                                cursor: 'pointer',
                                backgroundColor: blueGrey[100],
                              },
                            }}
                          >
                            <TableCell component="th" scope="row">
                              <Horse horse={hb.horse} showShowName />
                            </TableCell>
                            <TableCell>
                              {moment(hb.billableDate).format('MM/DD/YYYY')}
                            </TableCell>
                            <TableCell>{hb.note}{hb.quantity && ` (qty ${hb.quantity})`}</TableCell>
                            <TableCell align="right"><b>${parseFloat(hb.price).toFixed(2)}</b></TableCell>
                          </TableRow>
                        ))
                      }

                      {
                        groupHorseBillablesByHorse && !groupHorseBillablesByTitle && Object.keys(groupedByHorse).map((horseId) => (
                          <TableRow key={`invoice-billable-${horseId}`}>
                            <TableCell component="th" scope="row">
                              <Horse horse={groupedByHorse[horseId][0].horse} showShowName />
                            </TableCell>
                            <TableCell>
                              {groupedByHorse[horseId].map((hb) => moment(hb.billableDate).format('MM/DD/YYYY')).join(', ')}
                            </TableCell>
                            <TableCell>{uniqBy(groupedByHorse[horseId], 'note').map((hb) => hb.note).join(', ')}</TableCell>
                            <TableCell align="right"><b>${parseFloat(sumArray(groupedByHorse[horseId].map((hb) => hb.price))).toFixed(2)}</b></TableCell>
                          </TableRow>
                        ))
                      }
                      {
                        !groupHorseBillablesByHorse && groupHorseBillablesByTitle && Object.keys(groupedByType).map((note) => (
                          <TableRow key={`invoice-billable-${note}`}>
                            <TableCell component="th" scope="row">
                              {uniqBy(groupedByType[note], 'horse.id').map((hb) => (<Horse horse={hb.horse} showShowName key={`desc-horse-${hb.id}`} />))}
                            </TableCell>
                            <TableCell>
                              {groupedByType[note].map((hb) => moment(hb.billableDate).format('MM/DD/YYYY')).join(', ')}
                            </TableCell>
                            <TableCell>{note}</TableCell>
                            <TableCell align="right"><b>${parseFloat(sumArray(groupedByType[note].map((hb) => hb.price))).toFixed(2)}</b></TableCell>
                          </TableRow>
                        ))
                      }
                      {
                        isEditable && (
                          <TableRow
                            onClick={(e) => { setAnchorEl(e.currentTarget); setOpenBillablePopover(true); }}
                            sx={{
                              '&:hover': {
                                cursor: 'pointer',
                                backgroundColor: blueGrey[100],
                              },
                            }}
                          >
                            <TableCell component="th" scope="row">
                              <Button startIcon={<Add />}>Add a transaction</Button>
                            </TableCell>
                            <TableCell align="right">{validation.horseBillables && <Typography color="error">{validation.horseBillables}</Typography>}</TableCell>
                            <TableCell align="right" />
                          </TableRow>
                        )
                      }
                    </TableBody>
                  </Table>
                </TableContainer>
                <HorseBillableSelectPopover
                  anchorEl={anchorEl}
                  open={openBillablesPopover}
                  onClose={() => { setAnchorEl(null); setOpenBillablePopover(false); }}
                  selectedHorseBillables={horseBillables}
                  onChangeSelectedHorseBillables={(newHorseBillables) => setHorseBillables(newHorseBillables)}
                  setSelectedBillable={setSelectedBillable}
                  setOpenAddEditBillableModal={setOpenAddEditBillableModal}
                />
                <AddEditBillableModalComponent
                  open={openAddEditBillableModal}
                  billable={selectedBillable}
                  onClose={(newBillables) => {
                    if (!selectedBillable && newBillables && newBillables.length > 0) {
                      // new billable add to selected
                      setHorseBillables([...horseBillables, ...newBillables])
                    }
                    setOpenAddEditBillableModal(false);
                    setSelectedBillable(null);
                  }}
                />
                <Grid sx={{ mt: 4 }} container spacing={2}>
                  <Grid item xs={12}>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell align="right">Subtotal</TableCell>
                          <TableCell align="right" sx={{ width: 200 }}>${getSubtotal()}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell align="right">Discount</TableCell>
                          {
                            addDiscount
                              ? (
                                <TableCell align="right">
                                  <TextField
                                    disabled={isLoading || !isEditable}
                                    fullWidth
                                    sx={{
                                      width: 300,
                                      mb: 2,
                                    }}
                                    size="small"
                                    id="discount-description"
                                    label="Description (optional)"
                                    placeholder="Description (optional)"
                                    value={discountDescription}
                                    onChange={(e) => { setDiscountDescription(e.target.value); }}
                                  />
                                  <Tooltip title="Add either a discounted amount or precentage to apply to the total.">
                                    <InfoOutlined
                                      color="secondary"
                                      sx={{
                                        mr: 1,
                                        mt: 1,
                                      }}
                                    />
                                  </Tooltip>
                                  <TextField
                                    sx={{
                                      width: 100,
                                      mr: 2,
                                    }}
                                    size="small"
                                    id="discount amount"
                                    label="Amount"
                                    disabled={!isEditable}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    // eslint-disable-next-line react/jsx-no-duplicate-props
                                    inputProps={{ inputMode: 'numeric' }}
                                    type="number"
                                    value={parseFloat(discountAmount || '0', 10)}
                                    onChange={(e) => {
                                      let amount = parseFloat(e.target.value, 10);
                                      if (amount < 0) { amount = 0 }
                                      setDiscountAmount(amount);
                                      setDiscountPercent(0)
                                    }}
                                  />
                                  <Typography fontWeight={300} sx={{ mr: 2 }} component="span">OR</Typography>
                                  <TextField
                                    sx={{ width: 100 }}
                                    size="small"
                                    id="discount amount"
                                    label="Percentage"
                                    disabled={!isEditable}
                                    InputProps={{ endAdornment: <InputAdornment position="start">%</InputAdornment> }}
                                    // eslint-disable-next-line react/jsx-no-duplicate-props
                                    inputProps={{ inputMode: 'numeric' }}
                                    type="number"
                                    value={parseFloat(discountPercent || '0', 10)}
                                    onChange={(e) => {
                                      let percent = parseFloat(e.target.value, 10);
                                      if (percent < 0) { percent = 0 }
                                      if (percent > 100) { percent = 100 }
                                      setDiscountPercent(percent);
                                      setDiscountAmount(0)
                                    }}
                                  />
                                  {
                                    isEditable && (
                                      <Box
                                        sx={{
                                          textAlign: 'right',
                                          mt: 0.5,
                                        }}
                                      >
                                        <Button
                                          size="small"
                                          disabled={!isEditable}
                                          onClick={() => { setDiscountAmount(0); setDiscountPercent(0); setDiscountDescription(''); setAddDiscount(false); }}
                                        >Remove Discount
                                        </Button>
                                      </Box>
                                    )
                                  }
                                </TableCell>
                              )
                              : (
                                <TableCell align="right" sx={{ width: 200 }}>
                                  <Button
                                    size="small"
                                    disabled={!isEditable}
                                    startIcon={<Add />}
                                    onClick={() => setAddDiscount(true)}
                                  >Add Discount
                                  </Button>
                                </TableCell>
                              )
                          }
                        </TableRow>
                        <TableRow sx={{ backgroundColor: grey[100] }}>
                          <TableCell align="right"><b>Total</b></TableCell>
                          <TableCell align="right" sx={{ width: 200 }}>
                            <b>${getAmountDue()}</b>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>

                <Divider
                  sx={{
                    mt: 4,
                    mb: 4,
                  }}
                />
                <TextField
                  sx={{ mb: 2 }}
                  fullWidth
                  disabled={isLoading || !isEditable}
                  id="invoice-footer"
                  label="Customer Notes/Terms"
                  error={validation.footer}
                  helperText={(validation && validation.title)}
                  placeholder="Enter notes or terms of service that are visible to your customer"
                  value={notes}
                  multiline
                  rows={2}
                  onChange={(e) => { setNotes(e.target.value); }}
                />
              </Paper>

              {
                addingInvoiceError && <Alert severity="error">{addingInvoiceError}</Alert>
              }
            </DialogContent>
          )
      }

      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >

          {
            isEditable && status !== 'Draft' && (
              <Alert severity="info" sx={{ m: 2 }}>
                Changing the invoice status to <b>{status}</b> will finalize this invoice. You will not
                be able to change the fields, transactions, etc. You will still be able to change the status
                (Except back to Draft).
              </Alert>
            )
          }

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flex: 1,
              px: 2,
            }}
          >
            {(invoice && invoice.id)
              ? (
                <LoadingButton
                  color="error"
                  sx={{ mr: 2 }}
                  onClick={() => {
                    if (confirmDelete) { handleDelete(); } else { setConfirmDelete(true); }
                  }}
                  loading={isLoading}
                  variant={confirmDelete
                    ? 'contained'
                    : 'outlined'}
                >{confirmDelete
                  ? 'Confirm Delete Invoice'
                  : 'Delete Invoice'}
                </LoadingButton>
              )
              : (<Box />)}
            <Box>
              <Button onClick={() => onClose()} disabled={isLoading} sx={{ mr: 2 }}>Close</Button>
              {
                status === 'Open' && (
                  <Tooltip title="Check this flag to make sure the customers on the invoice are notified.">
                    <FormControlLabel
                      label="Send notification to customer(s)"
                      labelPlacement="end"
                      control={(
                        <Switch
                          checked={sendEmail}
                          onChange={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSendEmail(e.target.checked)
                          }}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      )}
                    />
                  </Tooltip>
                )
              }
              <LoadingButton onClick={onSave} disabled={isLoading} variant="contained" color="primary" loading={isLoading} loadingPosition="start" startIcon={<SaveIcon />}>Save</LoadingButton>
            </Box>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

AddEditInvoiceModalComponent.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  invoice: PropTypes.object,
};

AddEditInvoiceModalComponent.defaultProps = {
  open: false,
  invoice: null,
};

export default (AddEditInvoiceModalComponent);
